import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertCircle,
  ArrowUpRight,
  Barcode,
  Box,
  Building2,
  Check,
  CheckCircle2,
  Clock3,
  MapPin,
  PackageCheck,
  RefreshCw,
  Route,
  Store,
  Truck,
  UserRound,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import sclLogoUrl from "./assets/scl-logo.png";
import { dateTimeMoscow } from "./dateTime";
import {
  loadTracking,
  recordSenderPageClick,
  TrackingPackage,
  TrackingRecord,
  TrackingStatus,
  trackingToken,
  validTrackingToken,
} from "./api";
import "./styles.css";

const PickupBarcode = lazy(() => import("./PickupBarcode").then((module) => ({ default: module.PickupBarcode })));

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; tracking: TrackingRecord }
  | { kind: "error"; title: string; message: string };

const token = trackingToken();
function text(value: unknown, fallback = "-"): string {
  const normalized = value === null || value === undefined ? "" : String(value).trim();
  return normalized || fallback;
}

function formatDate(value: unknown): string {
  return dateTimeMoscow(value);
}

function formatWeightKg(value: unknown): string {
  const raw = text(value, "").replace(",", ".");
  if (!raw) return "";
  const number = Number(raw);
  if (!Number.isFinite(number) || number <= 0) return "";
  const kilograms = number >= 1000 ? number / 1000 : number;
  return `${kilograms.toLocaleString("ru-RU", {
    maximumFractionDigits: kilograms >= 10 ? 2 : 3,
  })} кг`;
}

function normalizedStatus(value: unknown): string {
  return text(value, "").toLocaleLowerCase("ru-RU");
}

function statusesNewestFirst(statuses: TrackingStatus[]): TrackingStatus[] {
  return statuses
    .map((status, index) => ({
      status,
      index,
      timestamp: Date.parse(text(status.status_date_time, "").replace(/([+-]\d{2})(\d{2})$/, "$1:$2")),
    }))
    .sort((left, right) => {
      const leftTimestamp = Number.isFinite(left.timestamp) ? left.timestamp : Number.NEGATIVE_INFINITY;
      const rightTimestamp = Number.isFinite(right.timestamp) ? right.timestamp : Number.NEGATIVE_INFINITY;
      return rightTimestamp - leftTimestamp || left.index - right.index;
    })
    .map(({ status }) => status);
}

function progressStage(statuses: TrackingStatus[], currentStatus: string): number {
  const value = normalizedStatus(currentStatus) || normalizedStatus(statuses[0]?.status_name);
  if (/(вручен|выдан получателю|доставлен|не вручен|возврат.*вручен)/.test(value)) return 4;
  if (/(до востребования|готов к выдаче|прибыл.*пвз|прибыл.*город|склад доставки|пункт выдачи)/.test(value)) return 3;
  if (/(транзит|отправлен|перевозчик|в пути|принят на склад|сортиров)/.test(value)) return 2;
  if (/(создан|принят)/.test(value)) return 1;
  return statuses.length ? 1 : 0;
}

function StatusIcon({ stage }: { stage: number }) {
  if (stage >= 4) return <CheckCircle2 aria-hidden="true" />;
  if (stage === 3) return <PackageCheck aria-hidden="true" />;
  if (stage === 2) return <Truck aria-hidden="true" />;
  return <Clock3 aria-hidden="true" />;
}

const progressSteps = [
  { label: "Создан", Icon: Box },
  { label: "В пути", Icon: Truck },
  { label: "В пункте выдачи", Icon: Store },
  { label: "Завершён", Icon: Check },
];

function Progress({ stage }: { stage: number }) {
  return (
    <section className="progress" aria-label="Этапы доставки">
      {progressSteps.map(({ label, Icon }, index) => {
        const step = index + 1;
        const className = stage === step ? "progress-step is-current" : stage > step ? "progress-step is-complete" : "progress-step";
        return (
          <div className={className} key={label}>
            <span className="progress-icon"><Icon aria-hidden={true} /></span>
            <span>{label}</span>
          </div>
        );
      })}
    </section>
  );
}

function Detail({
  label,
  value,
  Icon,
  wide = false,
  mono = false,
  children,
}: {
  label: string;
  value?: unknown;
  Icon: LucideIcon;
  wide?: boolean;
  mono?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <article className={`detail${wide ? " detail-wide" : ""}`}>
      <span className="detail-icon"><Icon aria-hidden={true} /></span>
      <div>
        <span className="detail-label">{label}</span>
        {children || <strong className={mono ? "mono" : ""}>{text(value)}</strong>}
      </div>
    </article>
  );
}

function PackageCard({ item, index }: { item: TrackingPackage; index: number }) {
  const dimensions = [item.length, item.width, item.height].filter((value) => text(value, ""));
  const chips = [
    formatWeightKg(item.weight),
    dimensions.length === 3 ? `${dimensions.join(" × ")} см` : "",
    Number(item.items_count || 0) > 0 ? `Товаров: ${item.items_count}` : "",
  ].filter(Boolean);
  return (
    <article className="package-card">
      <div className="package-title">
        <Box aria-hidden="true" />
        <strong>Грузоместо {text(item.number, String(index + 1))}</strong>
      </div>
      {chips.length ? (
        <div className="chips">
          {chips.map((chip) => <span key={chip}>{chip}</span>)}
        </div>
      ) : (
        <p className="secondary">Параметры не указаны</p>
      )}
      {text(item.comment, "") ? <p className="package-comment">{item.comment}</p> : null}
    </article>
  );
}

function Timeline({ statuses }: { statuses: TrackingStatus[] }) {
  if (!statuses.length) {
    return <p className="empty-copy">История движения пока не загружена.</p>;
  }
  return (
    <div className="timeline">
      {statuses.map((status, index) => {
        const meta = [text(status.city_name, ""), formatDate(status.status_date_time)].filter(Boolean);
        return (
          <article className="timeline-item" key={`${status.status_name}-${status.status_date_time}-${index}`}>
            <span className="timeline-dot" aria-hidden="true" />
            <div>
              <strong>{text(status.status_name, "Статус СДЭК")}</strong>
              {meta.length ? <p>{meta.join(" · ")}</p> : null}
            </div>
          </article>
        );
      })}
    </div>
  );
}

function BrandFooter({ showTrackingNote = false }: { showTrackingNote?: boolean }) {
  return (
    <footer className="page-footer">
      {showTrackingNote ? (
        <p>Информация обновляется по данным службы доставки.</p>
      ) : null}
      <div className="developer-brand">
        <span className="scl-logo-window">
          <img src={sclLogoUrl} alt="SCL by DR.Mult" />
        </span>
        <span>Simple Cabinet Logistics · Простой Кабинет Логистики</span>
      </div>
    </footer>
  );
}

function LoadingState() {
  return (
    <main className="tracking-shell" aria-busy="true">
      <header className="app-header">
        <div className="brand-mark"><Truck aria-hidden="true" /></div>
        <div><strong>Отслеживание</strong><span>Статус отправления</span></div>
      </header>
      <section className="loading-panel">
        <span className="spinner" aria-hidden="true" />
        <h1>Загружаем отправление</h1>
        <p>Получаем актуальный статус и историю движения.</p>
      </section>
      <BrandFooter />
    </main>
  );
}

function ErrorState({
  title,
  message,
  onRetry,
  showPendingHint = true,
}: {
  title: string;
  message: string;
  onRetry: () => void;
  showPendingHint?: boolean;
}) {
  return (
    <main className="tracking-shell">
      <header className="app-header">
        <div className="brand-mark"><Truck aria-hidden="true" /></div>
        <div><strong>Отслеживание</strong><span>Статус отправления</span></div>
      </header>
      <section className="error-panel">
        <span className="error-icon"><AlertCircle aria-hidden="true" /></span>
        <h1>{title}</h1>
        <p>{message}</p>
        {showPendingHint ? (
          <p className="secondary">Если заказ создан недавно, данные могут появиться чуть позже.</p>
        ) : null}
        <button className="button" type="button" onClick={onRetry}>
          <RefreshCw aria-hidden="true" /> Повторить
        </button>
      </section>
      <BrandFooter />
    </main>
  );
}

function TrackingPage({ tracking, onRefresh }: { tracking: TrackingRecord; onRefresh: () => void }) {
  const statuses = useMemo(
    () => statusesNewestFirst(Array.isArray(tracking.statuses) ? tracking.statuses : []),
    [tracking.statuses],
  );
  const packages = Array.isArray(tracking.packages) ? tracking.packages : [];
  const rawCdekNumber = text(tracking.cdek_number, "");
  const cdekNumber = rawCdekNumber || "Номер ещё не получен";
  const currentStatus = text(tracking.current_status, "Статус не загружен");
  const currentMeta = [text(tracking.current_status_city, ""), formatDate(tracking.current_status_at)].filter(Boolean);
  const stage = useMemo(() => progressStage(statuses, currentStatus), [statuses, currentStatus]);
  const senderUrl = text(tracking.sender_page_url, "");
  const validSenderUrl = /^https?:\/\//i.test(senderUrl);
  const [barcodeOpen, setBarcodeOpen] = useState(false);

  useEffect(() => {
    document.title = `${cdekNumber} · SCL`;
  }, [cdekNumber]);

  const onSenderClick = useCallback(() => recordSenderPageClick(token), []);

  return (
    <main className="tracking-shell">
      <header className="app-header">
        <div className="brand-mark"><Truck aria-hidden="true" /></div>
        <div className="brand-copy">
          <strong>Отслеживание</strong>
          <span>{text(tracking.tenant_name, "Отправление СДЭК")}</span>
        </div>
        <button className="icon-button" type="button" onClick={onRefresh} aria-label="Обновить данные" title="Обновить">
          <RefreshCw aria-hidden="true" />
        </button>
      </header>

      <section className="shipment-head">
        <div>
          <span className="eyebrow">Накладная СДЭК</span>
          <h1 className="mono">{cdekNumber}</h1>
          <p>{text(tracking.recipient_name, "Получатель не указан")}</p>
        </div>
        <div className="shipment-actions">
          <span className="live-state"><span aria-hidden="true" /> Актуальный статус</span>
          {rawCdekNumber ? (
            <button className="barcode-button" type="button" onClick={() => setBarcodeOpen(true)}>
              <Barcode aria-hidden="true" />
              Показать штрихкод
            </button>
          ) : null}
        </div>
      </section>

      <section className="current-status">
        <span className="status-icon"><StatusIcon stage={stage} /></span>
        <div>
          <span className="eyebrow">Сейчас</span>
          <h2>{currentStatus}</h2>
          {currentMeta.length ? <p>{currentMeta.join(" · ")}</p> : null}
        </div>
      </section>

      <Progress stage={stage} />

      <section className="details-grid" aria-label="Информация об отправлении">
        <Detail label="Отправитель" value={tracking.tenant_name} Icon={Building2}>
          {validSenderUrl ? (
            <a className="sender-link" href={senderUrl} target="_blank" rel="noopener noreferrer" onClick={onSenderClick}>
              {text(tracking.tenant_name)} <ArrowUpRight aria-hidden="true" />
            </a>
          ) : <strong>{text(tracking.tenant_name)}</strong>}
        </Detail>
        <Detail label="Заказ продавца" value={tracking.moysklad_order_number} Icon={Route} mono />
        <Detail label="Получатель" value={tracking.recipient_name} Icon={UserRound} />
        <Detail label="Город" value={tracking.city_name} Icon={MapPin} />
        <Detail
          label="Пункт выдачи"
          value={[text(tracking.pvz_code, ""), text(tracking.pvz_address, "")].filter(Boolean).join(" · ")}
          Icon={Store}
          wide
        />
      </section>

      <section className="content-section">
        <div className="section-title">
          <div><h2>Грузоместа</h2><p>Вес и габариты отправления</p></div>
          <span>{packages.length}</span>
        </div>
        {packages.length ? (
          <div className="packages-grid">
            {packages.map((item, index) => <PackageCard item={item} index={index} key={`${item.number}-${index}`} />)}
          </div>
        ) : <p className="empty-copy">Грузоместа не указаны.</p>}
      </section>

      <section className="content-section">
        <div className="section-title">
          <div><h2>История движения</h2><p>Сначала самые свежие события</p></div>
          <span>{statuses.length}</span>
        </div>
        <Timeline statuses={statuses} />
      </section>

      <BrandFooter showTrackingNote />
      {barcodeOpen ? (
        <Suspense fallback={<div className="barcode-overlay" role="status" aria-live="polite"><span className="spinner" aria-hidden="true"/><span className="sr-only">Готовим штрихкод</span></div>}>
          <PickupBarcode cdekNumber={rawCdekNumber} onClose={() => setBarcodeOpen(false)} />
        </Suspense>
      ) : null}
    </main>
  );
}

function App() {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!validTrackingToken(token)) {
      setState({
        kind: "error",
        title: "Ссылка некорректна",
        message: "Проверьте, что ссылка скопирована полностью.",
      });
      return;
    }
    const controller = new AbortController();
    setState({ kind: "loading" });
    loadTracking(token, controller.signal)
      .then((tracking) => setState({ kind: "ready", tracking }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        const knownMessage = error instanceof Error && error.message === "Отправление не найдено"
          ? error.message
          : "Не удалось загрузить данные. Проверьте подключение и повторите попытку.";
        setState({
          kind: "error",
          title: "Отправление не найдено",
          message: knownMessage,
        });
      });
    return () => controller.abort();
  }, [refreshKey]);

  const retry = useCallback(() => setRefreshKey((value) => value + 1), []);
  if (state.kind === "loading") return <LoadingState />;
  if (state.kind === "error") {
    return (
      <ErrorState
        title={state.title}
        message={state.message}
        onRetry={retry}
        showPendingHint={validTrackingToken(token)}
      />
    );
  }
  return <TrackingPage tracking={state.tracking} onRefresh={retry} />;
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
